namespace Solovis.Site.RequestNewInvestment {

  export interface EntityDefaultOptions {
    name?: string;
    localCurrencyId?: number;
  }

  export class RequestNewInvestmentControl extends Solovis.UI.BaseControl {

    public _portalServices: Solovis.Portal.IPortalServices;

    public _investmentEditModel;
    public _isDirty;
    public errorText;
    public _formBuilder: Solovis.UI.InputControls.FormBuilder;
    private _defaultOwnerEntityId: number | null = null;

    private _formNames = {
      name: "name",
      owner: "owner",
      holding: "holding",
      liquidityModel: "liquidity model",
      properties: "properties",
      fileUpload: "file upload",
    };


    constructor(options: Solovis.UI.BaseControlOptions) {
      super(options);

      this._portalServices = new Solovis.Portal.SitePortalServices();

      this._isDirty = false;
    }

    public async doInitAsync(): Promise<void> {
      const response = await this._portalServices.rpc().invoke(
        "~/DataImports/SpreadsheetTemplate/GetNewInvestmentRequestTemplate",
        null
      );
      const ownerDefault = response?.result?.owner?.default;
      if (ownerDefault != null) {
        try {
          const entityResponse = await this._portalServices.rpc().invoke(
            "~/Selectors/EntitySelector/GetEntityData",
            { entityId: ownerDefault }
          );
          if (entityResponse?.result) {
            this._defaultOwnerEntityId = ownerDefault;
          }
        } catch {
          // entity not found — skip default
        }
      }

    }

    public doRender() {
      const self = this;

      this.container.addClass("value-editor");

      if (this.errorText) {
        this.container.text(this.errorText);
        return;
      }

      const formDiv = $("<div>").appendTo(this.container);
      const formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: formDiv,
        labelColumnWidth: 150,
        width: 500,
        portalServices: this._portalServices
      });

      let propReportControl: any = null;

      formBuilder.addEntry({
        name: this._formNames.owner,
        label: "Owner",

        initialValue: (this._investmentEditModel && this._investmentEditModel.ownerId)
          ? { entityId: this._investmentEditModel.ownerId }
          : this._defaultOwnerEntityId !== null ? { entityId: this._defaultOwnerEntityId } : null,

        createEditor: (createEditorOptions) => {
          createEditorOptions.container
            .css("padding-top", "10px");

          const editorDiv = $("<div>").appendTo(createEditorOptions.container);

          const editorControl = new Solovis.UI.InputControls.ParameterInputControl({
            controlHost: editorDiv,
            parameterType: "entity",
            value: createEditorOptions.initialValue,
            required: true,
            validationModel: createEditorOptions.validationModel,
            portalServices: this._portalServices
          });

          editorControl.setAsCurrentControl();

          return editorControl;
        }
      });

      formBuilder.addEntry({
        name: this._formNames.holding,
        label: "Holding",

        initialValue: (this._investmentEditModel && this._investmentEditModel.holdingId) ? { entityId: this._investmentEditModel.holdingId } : null,

        createEditor: (createEditorOptions) => {
          createEditorOptions.container
            .css("padding-top", "10px");

          const editorDiv = $("<div>").appendTo(createEditorOptions.container);

          const editorControl = new Solovis.UI.InputControls.ParameterInputControl({
            controlHost: editorDiv,
            parameterType: "entity",
            value: createEditorOptions.initialValue,
            required: true,
            validationModel: createEditorOptions.validationModel,
            portalServices: this._portalServices
          });

          editorControl.setAsCurrentControl();

          const newHoldingLink = $("<a>")
              .css("font-size", "12px")
              .css("float", "right")
              .text("Add new holding")
              .appendTo(createEditorOptions.container);

          Solovis.UI.DomHelper.focusableClick(newHoldingLink, () => {
              new Solovis.Site.RequestNewInvestment.AddEntityDialog({
                  //TODO maybe add initial value so that we can pull it back up
                  saveCallback: function (savedEntity) {
                      editorControl.setValue(savedEntity);
                  },
              });
          });

          return editorControl;
        }
      });

      formBuilder.addEntry({
        name: this._formNames.name,
        label: "Name",

        editor: {
          type: "text"
        },
      });

      formBuilder.addEntry({
        name: this._formNames.liquidityModel,
        label: "Liquidity Model",

        createEditor: (createEditorOptions) => {
          createEditorOptions.container.css("padding-top", "10px");

          const editorDiv = $("<div>").appendTo(createEditorOptions.container);

          const editorControl = new Solovis.UI.InputControls.ParameterInputControl({
            controlHost: editorDiv,
            parameterType: "liquidityModel",
            value: createEditorOptions.initialValue,
            required: false,
            validationModel: createEditorOptions.validationModel,
            portalServices: this._portalServices,
          });

          editorControl.setAsCurrentControl();

          return editorControl;
        },
      });

      formBuilder.bindValueChanged((event, {formEntry, formBuilder}) => {
        const ownerValue = formBuilder.getEntryByName(this._formNames.owner).value();
        const holdingValue = formBuilder.getEntryByName(this._formNames.holding).value();
        const nameValue = formBuilder.getEntryByName(this._formNames.name).value() ?? "";

        if (formEntry.name === this._formNames.owner || formEntry.name === this._formNames.holding) {
          if (holdingValue && ownerValue) {
            const nameInput = $(formBuilder.getEntryByName(this._formNames.name)._valueDiv).find("input");
            nameInput.attr("placeholder", `${ownerValue.label} / ${holdingValue.label}`)
          }
        }

        const propertiesTable = propReportControl?.getReportPartControl?.("NewInvestmentProperties")
          ?.reportControlHost?.currentControl?.getReportPartControl?.("PropertyValueTable");
        const properties = propertiesTable?.dataSets?.values?.properties;

        if (!properties) {
          return;
        }

        const importCodeRow = properties.find(
          (p: any) => p.propertyDefinitionId === InvData.SystemPropertyDefinitionIds.DataImport.ImportCode
        );

        if (!importCodeRow) {
          return;
        }

        let nextImportCode: string | null = null;

        if (formEntry.name === this._formNames.name) {
          nextImportCode = nameValue;
        } else if (formEntry.name === this._formNames.owner || formEntry.name === this._formNames.holding) {
          if (!nameValue) {
            nextImportCode = (ownerValue && holdingValue) ? `${ownerValue.label} / ${holdingValue.label}` : "";
          }
        }

        if (nextImportCode === null) {
          return;
        }

        const currentImportCode = importCodeRow.value?.s?.[0] ?? "";

        if (nextImportCode === currentImportCode)
        {
          return;
        }

        InvData.Util.updatePropertyValues(importCodeRow, [nextImportCode]);
        propertiesTable.updateDataSets(propertiesTable.dataSets);
      });

      formBuilder.addEntry({
        name: this._formNames.properties,
        showValueOnFullRow: true,

        createEditor: (createEditorOptions) => {
          propReportControl = new (Solovis as any).Reports.ReportControl({
            controlHost: createEditorOptions.container,

            hiddenParameters: ["resourceId"],

            report: {
              autoRun: true,
              hideConfigSection: true,
              root: {
                layout: "BlockWithHeader",

                header: {
                  headerText: "Properties"
                },

                content: {
                  elementName: "NewInvestmentProperties",

                  partControl: "Solovis.Reports.Parts.SubReportControl",

                  settings: {
                    usePersistedSettings: true,

                    reportOptions: function () {
                      const newInvestmentPropertyValuesReportCode = "37D0A47B-1D5B-4532-8AF1-98BE2EDF1B5C";

                      const options = {
                        reportCode: newInvestmentPropertyValuesReportCode,
                        hideConfigSection: true,

                        autoRun: true,
                        collapseConfigSection: true
                      };

                      return options;
                    }
                  }
                }
              },
              fullWidth: true
            }
          });

          propReportControl.setAsCurrentControl();

          const getValue = (): Array<{ propertyDefinitionId: number, required: boolean, values: Array<any>, fromDayId: number }> => {
              const investmentProperties = propReportControl.getReportPartControl("NewInvestmentProperties");
              const dataTable = investmentProperties.reportControlHost.currentControl.getReportPartControl("PropertyValueTable");
              const properties = dataTable.dataSets.values.properties.map(prop => 
                ({
                  required: prop.required,
                  propertyDefinitionId: prop.property.id,
                  values: prop.value[(Object.keys(prop.value) as any).find(val => val !== "from")],
                  fromDayId: prop.value.from,
                })).filter(prop => prop.required || prop.values.length);

              return properties;
          }

          return {
            ...propReportControl,
            getValue: getValue,
            isValid: () => {
              const properties = getValue();
              const invalid = properties.some(prop => prop.required && !prop.values.length);

              if (invalid) {
                createEditorOptions.validationModel.setErrorMessage("A required property is missing a value.");
              }
              return !invalid;
            }
          };
        }
      });

      const MaxFilesSize = 15 * 1048576; // max total file size in bytes

      formBuilder.addEntry({
        name: this._formNames.fileUpload,
        showValueOnFullRow: true,
        createEditor: (createEditorOptions) => {
          const fileUploadControlHost = new Solovis.UI.ControlHost({
            container: createEditorOptions.container
          });
          const uploadControl = new Solovis.UI.FileUploadControl({
            accept: ".pdf, .msg, .xls, .xlsx, .png, .jpg, .csv",
            allowMultipleFiles: true,
            maxTotalSize: MaxFilesSize,
            maxSize: MaxFilesSize,
          });
          fileUploadControlHost.setCurrentControl(uploadControl);
          return {
            ...fileUploadControlHost, 
            getValue: () => uploadControl._files
          };
        }
      });

      formBuilder.setupDone();

      $(formBuilder).bind("valueChanged", function () {
        self._isDirty = true;
        self.clearNotification();
      });

      this._formBuilder = formBuilder;
    }

    public hideEditFields() {
      this._formBuilder.showEntries(false);
    }


    public focus() {
      let self = this;
      this.callAfterRender(function () {
        if (self._formBuilder) {
          self._formBuilder.getEntryByName(this._formNames.owner).focus();
        }
      });
    };

    public getInvestmentModel() {
      const owner = this._formBuilder.getValueByName(this._formNames.owner);
      const holding = this._formBuilder.getValueByName(this._formNames.holding);
      const name = this._formBuilder.getValueByName(this._formNames.name);
      const liquidityModelValue = this._formBuilder.getValueByName(this._formNames.liquidityModel);
      const propertyValues = this._formBuilder.getValueByName(this._formNames.properties);
      const fileUploadValues = this._formBuilder.getValueByName(this._formNames.fileUpload);

      const isNewHolding = holding.entityId === -1;
      const liquidityModelId = liquidityModelValue && typeof liquidityModelValue.id === "number"
        ? liquidityModelValue.id
        : undefined;

      const model: any = {
        ownerEntityId: owner.entityId,
        holdingEntityId: isNewHolding ? undefined : holding.entityId,
        newEntity: isNewHolding ? {
          name: holding.name,
          localCurrencyId: holding.localCurrencyId,
          specialEntityTypeId: holding.specialEntityTypeId,
          propertyValues: holding.propertyValues,
        } : undefined,
        name: name,
        propertyValues: propertyValues,
        fileUpload: fileUploadValues,
      };

      if (liquidityModelId !== undefined) {
        model.liquidityModelId = liquidityModelId;
      }

      return model;
    }

    public clearNotification() {
      this._formBuilder.formValidationModel.clearState();
    }

    public notify(options) {
      const self = this;

      if (options.success) {
        this.hideEditFields();

        const successHtml = $("<div>");

        // link to investment
        $("<a>")
          .text(options.investment.label)
          .attr("href", () => {
            const reportLink = this._portalServices.reportLinker().getReportLink({
              reportAlias: "AssetInfo",
              parameters: { entityOrInvestment: { investmentId: options.investment.investmentId } },
              autoRun: true,
              collapseConfigSection: true
            });
            return reportLink;
          })
          .appendTo(successHtml);

        successHtml
          .prepend("Investment ")
          .append(" saved.");

        const state = {
          success: {
            html: successHtml
          }
        };

        this._formBuilder.formValidationModel.state(state);
      }
      else {
        this._formBuilder.formValidationModel.setErrorMessage(options.message);
      }
    }

    public isDirty() {
      return this._isDirty;
    }

    public isValid() {
      return !this._formBuilder.hasErrors();
    }
  }

}